<template>
  <div class="steel-knight" :class="{ 'is-warlord': variant === 'warlord', 'is-lancer': variant === 'lancer', 'is-crowned': crowned && assembled, 'is-left': side === 'left', 'is-scattered': !assembled, 'is-forming': forming, 'is-awake': assembled, 'is-dragging': dragging, 'is-fighting': fighting, 'is-in-range': attackReady, 'is-walking': walking, 'is-fetching-shield': fetchingShield, ['skill-' + skill]: Boolean(skill), 'is-falling': falling, 'is-victorious': victorious }" :style="knightStyle" aria-label="靈魂盔甲騎士" @pointerdown="startDrag" @pointermove="moveDrag" @pointerup="endDrag" @pointercancel="cancelDrag" @lostpointercapture="cancelDrag">
    <span v-if="fighting || victorious" class="knight-battle-label" role="status">{{ fighting ? '⚔ 戰鬥中' : '✦ 勝利！' }}</span>
    <span v-if="fighting && attackReady" class="knight-impact" aria-hidden="true"><b>{{ sneaking ? '偷襲！' : '鏘！' }}</b><b>鏗！</b><b>砰！</b></span>
    <span v-if="fighting && skill === 'tornado'" class="knight-tornado" aria-hidden="true"><i v-for="n in 5" :key="n"></i></span>
    <span v-if="fighting && skill === 'blade-wave'" class="knight-blade-wave" aria-hidden="true"></span>
    <div v-if="variant === 'warlord' || variant === 'lancer'" class="knight-cape"></div>
    <span v-if="variant === 'lancer'" class="helmet-plume" aria-hidden="true"></span>
    <div class="knight-soul" aria-hidden="true"><span></span></div>
    <div class="knight-leg leg-left"></div>
    <div class="knight-leg leg-right"></div>
    <div class="knight-arm arm-right"></div>
    <div class="knight-body"><i></i><i></i><i></i></div>
    <div class="knight-shoulder shoulder-left"></div>
    <div class="knight-shoulder shoulder-right"><b v-for="n in 3" :key="n" :class="'shoulder-spike-' + n"></b></div>
    <button type="button" class="knight-head" :aria-label="assembled ? '盔甲騎士已甦醒，可拖曳戰鬥' : `喚醒盔甲靈魂（${awakenClicks}/3），點擊頭部`" @click="awaken">
      <span v-if="crowned && assembled" class="knight-crown" aria-label="寶箱冠軍皇冠"><i></i></span>
      <span v-if="variant === 'warlord'" class="warlord-horns"><i></i><i></i></span>
      <span v-if="side === 'left'" class="helmet-horns"><i></i><i></i><i></i></span>
      <span v-if="side === 'left'" class="helmet-visor"></span>
      <span class="knight-eye eye-left"></span><span class="knight-eye eye-right"></span>
      <span class="knight-nose"></span><span class="knight-mouth"></span>
    </button>
    <div class="knight-flail">
      <div class="knight-arm arm-left"></div>
      <div class="knight-flail-handle"></div>
      <div class="knight-mace-collar"></div>
      <div v-if="variant === 'warlord'" class="knight-sword"></div>
      <div v-if="variant === 'lancer'" class="knight-lance"><i></i></div>
      <div v-if="side === 'left' && variant !== 'lancer'" class="knight-chain-swing">
        <div class="knight-chain"><i v-for="n in 6" :key="n"></i></div>
        <div class="knight-mace"><i v-for="n in 6" :key="n" :style="{ '--spike': n }"></i><b class="mace-front-spike"></b></div>
      </div>
      <div v-else class="knight-mace"><i v-for="n in 6" :key="n" :style="{ '--spike': n }"></i><b class="mace-front-spike"></b></div>
      <div class="knight-hand"><i></i></div>
    </div>
    <div v-if="side !== 'left' && !shieldDropped" class="knight-shield"><div class="shield-skull"><i></i><i></i><b></b></div></div>
    <div v-else class="knight-free-hand"><i></i></div>
  </div>
</template>

<script>
import { knights, equipment } from './knightArena'
export default {
  name: 'SteelKnight',
  props: { side: { type: String, default: 'right' }, variant: { type: String, default: 'guardian' } },
  data () { return { awakenClicks: 3, assembled: true, forming: false, assemblyTimer: null, position: null, dragging: false, dragStart: null, fighting: false, victorious: false, battleTimer: null, duelMoveTimer: null, victoryTimer: null, opponent: null, arenaLocked: false, attackReady: false, rangeFrame: null, walking: false, stepTimer: null, crowned: false, sneaking: false, rangedReady: false, projectileDistance: 190, dashDistance: 70, skill: null, nextSkillAt: 0, skillTimer: null, shieldDropped: false, fetchingShield: false, shieldDropTimer: null, shieldPickupTimer: null, shieldPrepareTimer: null, falling: false, defeatTimer: null } },
  computed: {
    knightStyle () {
      return { '--soul-power': this.awakenClicks / 3, '--projectile-distance': this.projectileDistance + 'px', '--dash-distance': this.dashDistance + 'px', zIndex: this.arenaLocked ? 1060 : undefined, transition: (this.arenaLocked || this.fetchingShield || ['shield-retreat', 'blade-retreat'].includes(this.skill)) ? 'left 1.2s ease-in-out, top 1.2s ease-in-out' : undefined, ...(this.fighting && this.position && this.opponent && this.opponent.position ? { scale: this.position.x < this.opponent.position.x ? '-.5 .5' : '.5 .5', '--battle-label-scale': this.position.x < this.opponent.position.x ? -1 : 1 } : {}), ...(this.position ? { left: (this.position.x - 54) + 'px', top: (this.position.y - 140) + 'px', right: 'auto' } : {}) }
    }
  },
  watch: {
    position (next, previous) {
      window.clearTimeout(this.stepTimer)
      this.walking = false
      if ((!this.arenaLocked && !this.fetchingShield && !['shield-retreat', 'blade-retreat'].includes(this.skill)) || !this.assembled || this.forming || this.dragging) return
      if (next && previous && Math.hypot(next.x - previous.x, next.y - previous.y) < 3) return
      this.walking = true
      this.stepTimer = window.setTimeout(() => { this.walking = false }, 1200)
    },
    fighting (active) {
      window.clearTimeout(this.skillTimer)
      if (!active) this.resetShield()
      this.skill = null
      this.nextSkillAt = 0
      window.cancelAnimationFrame(this.rangeFrame)
      this.attackReady = false
      this.rangedReady = false
      if (active) this.checkAttackRange()
    }
  },
  mounted () { knights.add(this) },
  beforeDestroy () {
    window.clearTimeout(this.defeatTimer)
    this.resetShield()
    window.clearTimeout(this.skillTimer)
    window.clearTimeout(this.stepTimer)
    window.cancelAnimationFrame(this.rangeFrame)
    window.clearTimeout(this.assemblyTimer)
    window.clearTimeout(this.battleTimer)
    window.clearTimeout(this.duelMoveTimer)
    window.clearTimeout(this.victoryTimer)
    if (this.opponent) { this.opponent.fighting = false; this.opponent.arenaLocked = false; this.opponent.opponent = null }
    knights.delete(this)
  },
  methods: {
    sufferDefeat () {
      window.clearTimeout(this.defeatTimer)
      window.clearTimeout(this.skillTimer)
      this.resetShield()
      this.skill = null
      this.fighting = this.victorious = this.walking = false
      this.falling = true
      this.defeatTimer = window.setTimeout(() => {
        this.falling = false
        this.assembled = false
        this.awakenClicks = 0
      }, 1500)
    },
    resetShield () {
      window.clearTimeout(this.shieldPrepareTimer)
      window.clearTimeout(this.shieldDropTimer)
      window.clearTimeout(this.shieldPickupTimer)
      equipment.shields = equipment.shields.filter(shield => shield.owner !== this._uid)
      this.shieldDropped = this.fetchingShield = false
    },
    prepareRangedAttack (attack = 'shield-throw') {
      const target = this.opponent
      const own = this.$el.getBoundingClientRect(), other = target.$el.getBoundingClientRect()
      const away = own.left < other.left ? -1 : 1
      this.skill = attack === 'blade-wave' ? 'blade-retreat' : 'shield-retreat'
      this.nextSkillAt = Date.now() + 3500
      this.position = {
        x: Math.max(45, Math.min(window.innerWidth - 155, other.left + away * Math.max(280, Math.abs(own.left - other.left) + 90))),
        y: Math.max(30, Math.min(window.innerHeight - 180, other.top))
      }
      this.shieldPrepareTimer = window.setTimeout(() => {
        if (!this.fighting || !target.assembled || !knights.has(target)) { this.skill = null; return }
        this.opponent = target
        const current = this.$el.getBoundingClientRect(), destination = target.$el.getBoundingClientRect()
        this.projectileDistance = Math.max(190, Math.abs(current.left - destination.left) * 2)
        this.skill = attack
        if (attack === 'shield-throw') this.dropAndRetrieveShield()
        this.skillTimer = window.setTimeout(() => { this.skill = null }, attack === 'blade-wave' ? 1400 : 1000)
      }, 1250)
    },
    dropAndRetrieveShield () {
      const target = this.opponent
      this.shieldDropTimer = window.setTimeout(() => {
        if (!this.fighting || !target || !knights.has(target)) return
        const rect = target.$el.getBoundingClientRect()
        const drop = { owner: this._uid, x: Math.max(10, Math.min(window.innerWidth - 56, rect.left + 30)), y: Math.min(window.innerHeight - 40, rect.bottom + 6), gold: this.crowned }
        equipment.shields.push(drop)
        this.shieldDropped = true
        this.shieldPickupTimer = window.setTimeout(() => {
          if (!this.fighting || !this.assembled) { this.resetShield(); return }
          this.fetchingShield = true
          this.position = { x: Math.max(40, drop.x - 60), y: Math.max(20, drop.y - 130) }
          this.shieldPickupTimer = window.setTimeout(() => { this.resetShield() }, 1300)
        }, 500)
      }, 500)
    },
    useSkill () {
      const now = Date.now()
      if (this.fetchingShield || !this.fighting || (!this.attackReady && !this.rangedReady) || this.skill || now < this.nextSkillAt) return
      let choices
      if (!this.attackReady) {
        if (this.variant === 'lancer') choices = ['dodge', 'poke', 'lance-dive', 'charge', 'thrust']
        else if (this.variant === 'warlord') choices = ['blade-wave', 'thrust']
        else if (this.side !== 'left') choices = ['shield-throw', 'charge']
        else return
      } else if (this.variant === 'lancer') choices = this.opponent && this.opponent.attackReady
        ? ['poke', 'combo', 'dodge', 'dodge', 'lance-dive']
        : ['poke', 'poke', 'thrust', 'combo', 'lance-dive']
      else if (this.variant === 'warlord') choices = ['thrust', 'combo', 'jump', 'blade-wave']
      else if (this.side === 'left') choices = ['jump', 'sweep', 'combo', 'tornado']
      else choices = this.opponent && this.opponent.attackReady ? ['guard', 'charge', 'shield-throw'] : ['charge', 'slam', 'shield-throw']
      if (this.shieldDropped) choices = choices.filter(choice => choice !== 'shield-throw' && choice !== 'guard')
      if (!choices.length) return
      this.skill = choices[Math.floor(Math.random() * choices.length)]
      if (this.skill === 'shield-throw' || this.skill === 'blade-wave') { this.prepareRangedAttack(this.skill); return }
      if (this.skill === 'charge' || this.skill === 'thrust' || this.skill === 'lance-dive') {
        const own = this.$el.getBoundingClientRect(), other = this.opponent.$el.getBoundingClientRect()
        this.dashDistance = Math.max(35, Math.abs(own.left - other.left) - 135) * 2
      }
      this.nextSkillAt = now + (this.skill === 'lance-dive' ? 2600 : 1600) + Math.random() * 900
      this.skillTimer = window.setTimeout(() => { this.skill = null }, this.skill === 'lance-dive' ? 1800 : 1000)
    },
    checkAttackRange () {
      if (!this.fighting) return
      const target = this.opponent
      if (target && knights.has(target) && target.assembled && target.fighting) {
        const own = this.$el.getBoundingClientRect()
        const other = target.$el.getBoundingClientRect()
        const gapX = Math.max(0, own.left - other.right, other.left - own.right)
        const gapY = Math.max(0, own.top - other.bottom, other.top - own.bottom)
        const heightDifference = Math.abs((own.top + own.bottom) / 2 - (other.top + other.bottom) / 2)
        this.rangedReady = gapX <= 400 && heightDifference <= 25
        this.attackReady = Math.hypot(gapX, gapY) <= (this.attackReady ? 85 : 65) &&
          heightDifference <= (this.attackReady ? 25 : 18)
      } else {
        this.attackReady = false
        this.rangedReady = false
      }
      if (this.attackReady || this.rangedReady) this.useSkill()
      this.rangeFrame = window.requestAnimationFrame(() => this.checkAttackRange())
    },
    startDrag (event) {
      if (this.fetchingShield || this.arenaLocked || !this.assembled || this.forming || this.fighting || event.button !== 0) return
      const box = this.$el.getBoundingClientRect()
      this.dragStart = { x: event.clientX, y: event.clientY, left: box.left, top: box.top }
      this.dragging = true
      this.$el.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    moveDrag (event) {
      if (!this.dragging) return
      this.position = {
        x: Math.max(40, Math.min(window.innerWidth - 148, this.dragStart.left + event.clientX - this.dragStart.x)),
        y: Math.max(20, Math.min(window.innerHeight - 170, this.dragStart.top + event.clientY - this.dragStart.y))
      }
    },
    cancelDrag () { this.dragging = false; this.dragStart = null },
    endDrag (event) {
      if (!this.dragging) return
      this.cancelDrag()
      if (this.$el.hasPointerCapture(event.pointerId)) this.$el.releasePointerCapture(event.pointerId)
      const box = this.$el.getBoundingClientRect()
      const target = [...knights].find(other => {
        if (other === this || other.arenaLocked || !other.assembled || other.forming || other.fighting || other.dragging || !other.$el.getClientRects().length) return false
        const rect = other.$el.getBoundingClientRect()
        return Math.abs(box.left - rect.left) < 75 && Math.abs(box.top - rect.top) < 100
      })
      if (target) this.battle(target)
    },
    moveDuel (target) {
      if (!knights.has(this) || !knights.has(target) || !this.fighting || !target.fighting) return
      const fighters = [this, target]
      const locations = fighters.map(knight => knight.$el.getBoundingClientRect())
      const driftX = (Math.random() - .5) * 160
      const baseline = (locations[0].top + locations[1].top) / 2 + (Math.random() - .5) * 65
      fighters.forEach((knight, index) => {
        if (knight.fetchingShield || ['dodge', 'poke', 'lance-dive', 'charge', 'thrust', 'shield-retreat', 'shield-throw', 'blade-retreat', 'blade-wave'].includes(knight.skill)) return
        const own = locations[index], other = locations[1 - index]
        const retreat = knight.attackReady && Math.random() < .3
        const side = own.left < other.left ? -1 : 1
        const gap = retreat ? 225 + Math.random() * 40 : 140 + Math.random() * 15
        knight.position = {
          x: Math.max(50, Math.min(window.innerWidth - 158, other.left + side * gap + driftX)),
          y: Math.max(140, Math.min(window.innerHeight - 200, baseline + (Math.random() - .5) * 12))
        }
        knight.$el.style.setProperty('--combat-tempo', (1.8 + Math.random() * 1.4) + 's')
      })
      this.duelMoveTimer = window.setTimeout(() => this.moveDuel(target), 900 + Math.random() * 300)
    },
    battle (target) {
      const ownBox = this.$el.getBoundingClientRect()
      const targetBox = target.$el.getBoundingClientRect()
      const spacing = 155
      const leftX = Math.max(40, Math.min(window.innerWidth - spacing - 148, (ownBox.left + targetBox.left) / 2 - spacing / 2))
      const top = Math.max(30, Math.min(window.innerHeight - 170, (ownBox.top + targetBox.top) / 2))
      const leftKnight = this.side === 'left' ? this : target
      const rightKnight = leftKnight === this ? target : this
      leftKnight.position = { x: leftX, y: top }
      rightKnight.position = { x: leftX + spacing, y: top }
      this.arenaLocked = target.arenaLocked = true
      this.fighting = target.fighting = true
      this.victorious = target.victorious = false
      this.opponent = target
      target.opponent = this
      this.duelMoveTimer = window.setTimeout(() => this.moveDuel(target), 1100)
      this.battleTimer = window.setTimeout(() => {
        if (!knights.has(target)) return
        const winner = Math.random() < .5 ? this : target
        const loser = winner === this ? target : this
        window.clearTimeout(this.duelMoveTimer)
        this.$el.style.removeProperty('--combat-tempo')
        target.$el.style.removeProperty('--combat-tempo')
        this.fighting = target.fighting = false
        this.opponent = target.opponent = null
        loser.sufferDefeat()
        this.battleTimer = window.setTimeout(() => {
          if (!knights.has(target)) return
          winner.victorious = true
          window.clearTimeout(winner.victoryTimer)
          winner.victoryTimer = window.setTimeout(() => {
            winner.victorious = false
            this.position = target.position = null
            this.arenaLocked = target.arenaLocked = false
          }, 2400)
        }, 1500)
      }, 15000)
    },
    awaken () {
      if (this.assembled || this.arenaLocked) return
      this.awakenClicks++
      if (this.awakenClicks === 3) {
        this.forming = true
        this.assembled = true
        this.assemblyTimer = window.setTimeout(() => { this.forming = false }, 1600)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.steel-knight {
  display: none;
  scale: .5 .5;
  --steel: linear-gradient(125deg, #e0e0d2 0%, #9b9e97 23%, #4d5554 48%, #bfc1b3 66%, #454d4b 100%);
  position: fixed;
  right: max(24px, calc((100vw - 1140px) / 4 - 105px));
  top: 360px;
  width: 216px;
  height: 280px;
  pointer-events: none;
  z-index: 1;
  filter: drop-shadow(3px 8px 5px rgba(0, 0, 0, .3));
  > div, > .knight-head { position: absolute; background: var(--steel); border: 3px solid #343d3b; box-shadow: inset 2px 2px 3px #e0e1d0, inset -3px -3px 5px #252e2c; }
}
body.classMasterHead .steel-knight { display: block; }
.steel-knight.is-left {
  right: auto;
  left: max(24px, calc((100vw - 1140px) / 4 - 105px));
  scale: -.5 .5;
}
.steel-knight {
  pointer-events: auto;
  animation: knightIdle 5s ease-in-out infinite;
  transform-origin: 50% 100%;
  .knight-head { animation: knightBreathe 4s ease-in-out infinite; }
  .knight-eye { animation: knightBlink 6s infinite, knightEyeGlow 4s ease-in-out infinite; }
  .knight-flail { animation: knightWeaponIdle 5s ease-in-out infinite; }
  .knight-shield { transition: transform .3s ease; transform-origin: 40% 30%; }
  &.is-awake:not(.is-forming):not(.is-fighting):not(.is-victorious):not(.is-dragging):hover {
    .knight-flail { animation: knightFlailSwing 1s ease-in-out infinite; }
    .knight-eye { filter: brightness(1.3) drop-shadow(0 0 3px #70eddf); }
  }
}
@keyframes knightIdle {
  0%, 100% { transform: translateY(0) rotate(-.4deg); }
  50% { transform: translateY(-2px) rotate(.4deg); }
}
@keyframes knightWeaponIdle {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}
@keyframes knightEyeGlow {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.2) drop-shadow(0 0 2px #70eddf); }
}
@keyframes knightFlailSwing {
  0%, 100% { transform: rotate(-10deg); }
  45% { transform: rotate(80deg); }
  65% { transform: rotate(68deg); }
}
@keyframes knightBreathe {
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-2px) rotate(-3deg); }
}
@keyframes knightBlink {
  0%, 44%, 48%, 100% { scale: 1 1; }
  46% { scale: 1 .12; }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight, .steel-knight .knight-flail { animation: none; }
  .steel-knight .knight-head, .steel-knight .knight-eye { animation: none; }
  .steel-knight .knight-shield { transition: none; }
  .steel-knight.is-awake:hover .knight-flail { animation: none; }
}
.knight-head {
  padding: 0; appearance: none; color: inherit; cursor: pointer;
  &:disabled { cursor: default; opacity: 1; }
  &:focus-visible { outline: 2px solid #70eddf; outline-offset: 5px; }
  left: 55px; top: 0; width: 106px; height: 106px;
  border-radius: 48% 48% 26% 26%; transform: rotate(-4deg); z-index: 4;
  &::before { content: ''; position: absolute; inset: 6px; border-radius: inherit; background: repeating-linear-gradient(145deg, transparent 0 17px, #252e2c40 18px, transparent 20px 29px); }
}
.knight-eye { position: absolute; top: 29px; width: 32px; height: 37px; border: 7px solid #252e2c; border-radius: 48%; background: radial-gradient(ellipse, #e0fff3 10%, #81f2dd 37%, #14a7bc 70%); box-shadow: 0 0 0 2px #878e7f, inset 0 0 5px #0ab3cb; }
.eye-left { left: 13px; }
.eye-right { right: 12px; }
.knight-nose { position: absolute; top: 68px; left: 43px; width: 14px; height: 12px; background: #222c29; clip-path: polygon(50% 0, 100% 100%, 50% 75%, 0 100%); }
.knight-mouth {
  position: absolute; top: 82px; left: 34px; width: 37px; height: 15px; z-index: 3;
  &::before {
    content: ''; position: absolute; left: 2px; top: 0; width: 5px; height: 15px;
    border-radius: 3px; background: #202925;
    box-shadow: 11px 0 #202925, 22px 0 #202925;
  }
}
.knight-body { left: 53px; top: 105px; width: 110px; height: 112px; border-radius: 15% 15% 30% 30%; z-index: 2; overflow: hidden;
  i { position: absolute; left: 10%; width: 80%; height: 27px; border-bottom: 5px ridge #90998e; transform: rotate(0deg); clip-path: polygon(0 0, 50% 36%, 100% 0, 100% 65%, 50% 100%, 0 65%); background: var(--steel); }
  i:nth-child(1) { bottom: 4px; } i:nth-child(2) { bottom: 24px; } i:nth-child(3) { bottom: 44px; }
}
.knight-shoulder { top: 100px; width: 51px; height: 61px; z-index: 3; }
.shoulder-left { left: 15px; border-radius: 70% 20% 35% 25%; transform: rotate(18deg); &::after { content: ''; position: absolute; inset: 6px; border-left: 5px ridge #c6c9b8; border-right: 5px ridge #c6c9b8; border-radius: inherit; } }
.shoulder-right {
  right: 10px; width: 55px; height: 63px;
  border-radius: 34% 70% 28% 24%; transform: rotate(-14deg);
  background: radial-gradient(ellipse at 32% 22%, #d2d5c8, #939c8f 30%, #505c52 65%, #303c34) !important;
  &::before {
    content: ''; position: absolute; inset: 4px 3px 7px 5px; z-index: 2;
    border: 3px ridge #aeb6a6; border-radius: 35% 70% 30% 22%;
    background: linear-gradient(145deg, #b3bcac 0%, #75816f 30%, #424f43 70%);
    box-shadow: inset 2px 2px 3px #d8dccd80, 1px 2px 2px #26332c;
  }
  &::after {
    content: ''; position: absolute; left: 23px; top: 18px;
    width: 17px; height: 17px; z-index: 3; border-radius: 50%;
    border: 2px solid #39463b;
    background: radial-gradient(circle at 34% 26%, #c7cebc, #7b8873 38%, #3d4b3e 80%);
    box-shadow: 0 0 0 2px #8c998380, 1px 3px 3px #243027;
  }
  b {
    position: absolute; width: 14px; height: 20px;
    transform-origin: center bottom;
    z-index: 0;
    &::before {
      content: ''; position: absolute; inset: 0 1px 3px;
      background: linear-gradient(105deg, #566159 12%, #c6cdbd 43%, #939e8d 52%, #414c47 85%);
      clip-path: polygon(48% 0, 67% 28%, 100% 90%, 77% 100%, 20% 100%, 0 90%, 31% 28%);
    }
    &::after {
      content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 6px;
      border: 1px solid #465148; border-radius: 50%;
      background: linear-gradient(#aeb8a5, #677461 65%, #455143);
      box-shadow: 0 2px 2px #26332c70;
    }
  }
  .shoulder-spike-1 { left: 24px; top: -7px; height: 17px; transform: rotate(22deg); }
  .shoulder-spike-2 { right: -4px; top: 12px; height: 18px; transform: rotate(64deg); }
  .shoulder-spike-3 { right: -4px; top: 34px; height: 16px; transform: rotate(103deg); }
}
.knight-arm { top: 146px; width: 36px; height: 58px; border-radius: 30%; }
.arm-left { left: 19px; transform: rotate(20deg); }.arm-right { right: 20px; transform: rotate(-20deg); }
.knight-leg { top: 203px; width: 38px; height: 65px; border-radius: 20% 20% 35% 20%; &::after { content: ''; position: absolute; bottom: -5px; left: -7px; width: 47px; height: 22px; background: var(--steel); border: 2px solid #343d3b; border-radius: 50% 25% 20% 20%; } }
.leg-left { left: 58px; transform: rotate(8deg); }.leg-right { right: 56px; transform: rotate(-8deg); }
.knight-shield { right: 2px; top: 145px; width: 90px; height: 108px; border-radius: 50%; z-index: 6; transform: rotate(-8deg);
  &::before { content: ''; position: absolute; inset: 8px; border: 4px dotted #3b4540; border-radius: 50%; box-shadow: 0 0 0 2px #b6beb0; }
}
.shield-skull { position: absolute; inset: 24px 19px; border-radius: 45%; border: 2px solid #46504a; background: var(--steel); i { position: absolute; top: 12px; width: 13px; height: 14px; background: #414b45; border: 2px solid #bfc6b7; border-radius: 50%; } i:first-child { left: 4px; } i:nth-child(2) { right: 4px; } b { position: absolute; bottom: 7px; left: 14px; width: 17px; height: 11px; background: repeating-linear-gradient(90deg, #344039 0 3px, transparent 3px 6px); } }
.steel-knight .knight-flail {
  inset: 0; z-index: 5; background: none; border: 0; box-shadow: none;
  transform-origin: 37px 146px; pointer-events: none;
  > div { position: absolute; background: var(--steel); border: 3px solid #343d3b; box-shadow: inset 2px 2px 3px #e0e1d0, inset -3px -3px 5px #252e2c; }
}
.steel-knight .knight-flail-handle {
  left: -19px; top: 178px; width: 65px; height: 13px;
  z-index: 4; border-radius: 4px;
  background: linear-gradient(0deg, #343e38, #aeb8a7 45%, #667361 70%, #343e38);
  &::after { content: ''; position: absolute; right: 1px; top: -3px; width: 7px; height: 15px; border: 2px solid #354138; border-radius: 3px; background: var(--steel); }
}
.steel-knight .knight-hand {
  left: 18px; top: 171px; width: 29px; height: 24px;
  border-radius: 8px; z-index: 6; transform: rotate(18deg);
  i { position: absolute; inset: 4px; background: repeating-linear-gradient(90deg, transparent 0 5px, #343d3b 5px 7px); }
}
.steel-knight .knight-mace-collar {
  left: -17px; top: 173px; width: 15px; height: 23px;
  border-radius: 5px; z-index: 5;
  background: linear-gradient(0deg, #344038, #c0c8b5 45%, #647260);
}
.steel-knight .knight-mace { left: -68px; top: 154px; width: 57px; height: 57px; border-radius: 50%; z-index: 5;
  background: radial-gradient(circle at 30% 24%, #d0d7c7, #7a877a 38%, #38483d 80%);
  &::before { content: ''; position: absolute; inset: 5px 9px; border-left: 2px solid #c0cbb780; border-right: 2px solid #26352c; border-radius: 50%; transform: rotate(-25deg); }
  &::after { content: ''; position: absolute; left: 11px; top: 12px; width: 4px; height: 4px; border-radius: 50%; background: #c3cdb9; box-shadow: 18px 4px 0 #aebda5, 5px 21px 0 #80937b, 1px 1px 1px #26352c, 19px 5px 1px #26352c, 6px 22px 1px #26352c; }
  i { position: absolute; left: 19px; top: 17px; width: 13px; height: 19px; background: var(--steel); clip-path: polygon(50% 0, 100% 100%, 0 100%); transform: rotate(calc(var(--spike) * 60deg + 20deg)) translateY(-28px); }
  .mace-front-spike { position: absolute; left: 17px; top: 20px; width: 16px; height: 17px; border-radius: 45%; background: radial-gradient(circle at 35% 25%, #e0e4d5, #8c9986 35%, #354337 80%); box-shadow: 1px 2px 2px #26352c; }
}
.steel-knight {
  > div, > .knight-head { transition: translate 1.2s cubic-bezier(.2, .75, .25, 1), rotate 1.2s ease, opacity 1s ease; }
  .knight-soul {
    left: 66px; top: 40px; width: 87px; height: 175px;
    z-index: 0; border: 0; border-radius: 45% 45% 35% 35%;
    background: radial-gradient(ellipse, #d9fff680 5%, #73eddb70 30%, #2bc6c930 55%, transparent 72%);
    box-shadow: none; opacity: 0; pointer-events: none;
    filter: blur(3px);
    span {
      position: absolute; left: 14px; top: -20px; width: 59px; height: 65px;
      border-radius: 48% 48% 42% 42%;
      background: radial-gradient(ellipse, #d9fff680, #72f3d850 45%, transparent 72%);
    }
  }
  &.is-scattered {
    animation: none;
    .knight-head { translate: -17px 95px; rotate: -22deg; animation: none; }
    .knight-body { translate: 0 115px; rotate: 12deg; }
    .shoulder-left { translate: -39px 115px; rotate: -40deg; }
    .shoulder-right { translate: 44px 123px; rotate: 40deg; }
    > .arm-right { translate: 48px 95px; rotate: 34deg; }
    .knight-flail { translate: -34px 80px; rotate: -28deg; animation: none; }
    .leg-left { translate: -20px 65px; rotate: -65deg; }
    .leg-right { translate: 12px 63px; rotate: 55deg; }
    .knight-shield { translate: 42px 102px; rotate: 42deg; }
    .knight-eye {
      animation: none;
      background: radial-gradient(ellipse, #d9fff6, #42cfcc 65%);
      filter: brightness(calc(.16 + var(--soul-power) * 1.8));
      box-shadow: 0 0 calc(var(--soul-power) * 18px) #70eddf;
      transition: filter .3s ease, box-shadow .3s ease;
    }
    .knight-soul { opacity: calc(var(--soul-power) * .7); }
  }
  &.is-forming {
    animation: none;
    .knight-head, .knight-flail { animation: none; }
    .knight-soul { z-index: 0; animation: knightSoulGather 1.6s ease both; }
    .knight-eye { filter: brightness(1.6) drop-shadow(0 0 9px #70eddf); }
  }
}
@keyframes knightSoulGather {
  0% { opacity: .5; transform: scale(.7); }
  30% { opacity: 1; transform: scale(1.2); }
  65% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(.85); }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight {
    > div, > .knight-head { transition: none; }
    &.is-forming .knight-soul { animation: none; }
  }
}
.steel-knight {
  &.is-awake { cursor: grab; touch-action: none; user-select: none; }
  &.is-awake .knight-head { cursor: inherit; }
  &.is-dragging { z-index: 1050; cursor: grabbing; animation: none; }
  &.is-fighting { z-index: 1051; animation: knightCombat .2s ease-in-out infinite; cursor: wait; }
  &.is-fighting .knight-flail { animation: knightFlailSwing .35s ease-in-out infinite; }
  &.is-victorious { animation: knightVictory .6s ease-in-out 4; }
  &.is-victorious .knight-flail { transform: rotate(110deg); animation: none; }
  .knight-battle-label {
    position: absolute; top: -44px; left: 25px; width: 170px;
    padding: 5px 8px; border: 1px solid #8de4d3; border-radius: 20px;
    text-align: center; color: #e1fff5; background: #293e37;
    font-size: 16px; font-weight: 700; pointer-events: none;
  }
  &.is-left .knight-battle-label { scale: -1 1; }
  .knight-impact {
    position: absolute; left: -45px; top: 105px; width: 140px; height: 100px;
    z-index: 10; pointer-events: none;
    b {
      position: absolute; left: 20px; top: 20px; opacity: 0;
      color: #fff2b1; font-size: 30px; font-weight: 900; white-space: nowrap;
      text-shadow: 2px 2px #3b3026, -1px -1px #3b3026, 0 0 10px #e7c569;
      animation: knightImpact 1.05s ease-out infinite;
    }
    b:nth-child(2) { left: 55px; top: -15px; animation-delay: .35s; }
    b:nth-child(3) { left: -5px; top: 45px; animation-delay: .7s; }
  }
  &.is-left .knight-impact { scale: -1 1; }
}
@keyframes knightImpact {
  0%, 65%, 100% { opacity: 0; transform: translateY(5px) scale(.6) rotate(-12deg); }
  8% { opacity: 1; transform: translateY(-3px) scale(1.2) rotate(-8deg); }
  30% { opacity: 1; transform: translateY(-12px) scale(1) rotate(-8deg); }
  55% { opacity: 0; transform: translateY(-25px) scale(1.05) rotate(-5deg); }
}
@keyframes knightCombat {
  0%, 100% { transform: translateX(-5px) rotate(-2deg); }
  50% { transform: translateX(5px) rotate(2deg); }
}
@keyframes knightVictory {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-14px) rotate(-3deg); }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight.is-fighting, .steel-knight.is-victorious,
  .steel-knight.is-fighting .knight-flail { animation: none; }
}
// The left guardian has a closed helmet and a chain flail.
.steel-knight.is-left {
  --steel: linear-gradient(125deg, #d2d4cb, #8c948b 26%, #3c4740 52%, #a9b1a2 72%, #3a453d);
  .knight-head {
    border-radius: 48% 48% 15% 15%; height: 111px;
    &::before { inset: 5px; border-radius: inherit; background: linear-gradient(110deg, transparent 30%, #eff3e120 45%, transparent 55%); }
    &::after { content: ''; position: absolute; left: 2px; right: 2px; bottom: 7px; height: 26px; border-top: 3px ridge #aab3a1; border-radius: 0 0 20% 20%; background: var(--steel); }
  }
  .helmet-visor { position: absolute; left: 6px; right: 6px; top: 44px; height: 23px; background: #111d1b; border: 2px solid #aeb7a7; border-radius: 30% 30% 20% 20%; box-shadow: inset 0 3px 4px #000; }
  .knight-eye { top: 47px; width: 21px; height: 15px; border: 2px solid #1a747c; border-radius: 0 0 50% 50%; }
  .eye-left { left: 17px; }.eye-right { right: 17px; }
  .knight-nose, .shoulder-right b { display: none; }
  .knight-mouth { display: none; }
  .helmet-horns {
    position: absolute; inset: 0;
    i { position: absolute; width: 15px; height: 19px; background: var(--steel); clip-path: polygon(50% 0, 100% 90%, 70% 100%, 20% 100%, 0 90%); }
    i:nth-child(1) { left: 43px; top: -18px; }
    i:nth-child(2) { left: 0; top: 0; rotate: -40deg; }
    i:nth-child(3) { right: 0; top: 0; rotate: 40deg; }
  }
  .knight-body {
    background: linear-gradient(120deg, #697568, #26332b 60%, #485447);
    border-radius: 15% 15% 10% 10%;
    i { width: 28%; height: 47px; bottom: 0; clip-path: polygon(15% 0, 85% 0, 100% 100%, 0 100%); border: 2px ridge #9ca893; transform: none; }
    i:nth-child(1) { left: 5%; } i:nth-child(2) { left: 36%; } i:nth-child(3) { left: 67%; }
  }
  .knight-shoulder { border-radius: 60% 60% 15% 20%; }
  .shoulder-right::after { left: 9px; top: 35px; width: 38px; height: 18px; border-radius: 5px; background: var(--steel); border: 2px ridge #9ca893; box-shadow: none; }
  .knight-free-hand { right: 12px; top: 187px; width: 36px; height: 32px; border-radius: 35%; z-index: 4;
    i { position: absolute; inset: 6px; border-radius: 50%; background: #16231c; border: 3px ridge #acb5a3; }
  }
  &.is-scattered .knight-free-hand { translate: 48px 95px; rotate: 34deg; }
  .knight-flail-handle { left: -19px; top: 178px; width: 65px; height: 13px; background: linear-gradient(0deg, #343e38, #aeb8a7 45%, #667361 70%, #343e38); }
  .knight-mace-collar { left: -23px; top: 173px; width: 15px; height: 23px; }
  .knight-chain {
    left: -37px; top: 105px; width: 73px; height: 28px;
    background: none; border: 0; box-shadow: none;
    i { position: absolute; width: 16px; height: 10px; border: 3px solid #8e9b86; border-radius: 50%; box-shadow: inset 1px 1px #dbe0d0, 0 1px #223027; }
    @for $link from 1 through 6 {
      i:nth-child(#{$link}) { left: ($link - 1) * 11px; top: 10px; }
    }
    i:nth-child(1) { top: 20px; rotate: -55deg; }
    i:nth-child(2) { top: 12px; rotate: -20deg; }
    i:nth-child(5) { top: 12px; rotate: 15deg; }
    i:nth-child(6) { top: 16px; rotate: 55deg; }
  }
  .knight-mace { left: -65px; top: 137px; }
  .knight-chain-swing {
    position: absolute; inset: 0; background: none; border: 0; box-shadow: none;
    transform-origin: 32px 122px;
    translate: -49px 63px;
    > div { position: absolute; }
    .knight-mace { border: 3px solid #343d3b; box-shadow: inset 2px 2px 3px #e0e1d0, inset -3px -3px 5px #252e2c; }
  }
  &.is-awake:not(.is-forming):not(.is-fighting):not(.is-victorious):not(.is-dragging):hover {
    .knight-flail { animation: knightChainArm 1.4s ease-in-out infinite; }
    .knight-chain-swing { animation: knightChainWhip 1.4s ease-in-out infinite; }
  }
  &.is-dragging .knight-flail, &.is-dragging .knight-chain-swing { animation: none; }
  &.is-victorious .knight-flail { animation: none !important; transform: rotate(25deg); }
}
@keyframes knightChainArm {
  0%, 100% { transform: rotate(-6deg); }
  30% { transform: rotate(22deg); }
  65% { transform: rotate(-18deg); }
}
@keyframes knightChainWhip {
  0%, 100% { transform: rotate(-8deg); }
  40% { transform: rotate(65deg); }
  75% { transform: rotate(-30deg); }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight.is-left.is-awake:not(.is-forming):hover .knight-flail,
  .steel-knight.is-left.is-awake:not(.is-forming):hover .knight-chain-swing,
  .steel-knight.is-left.is-fighting .knight-flail,
  .steel-knight.is-left.is-fighting .knight-chain-swing { animation: none; }
}
// Both weapons point to local left; mirroring the left guardian points it inward.
.steel-knight.is-fighting {
  scale: .5 .5;
  animation: knightBattleCombo 2.8s ease-in-out infinite;
  .knight-flail { animation: knightBattleWeaponCombo 2.8s ease-in-out infinite !important; }
  .knight-head { animation: knightBattleHead 2.8s ease-in-out infinite; }
  .knight-shield { animation: knightBattleGuard 2.8s ease-in-out infinite; }
  .knight-impact b { animation-duration: 1.4s; }
  .knight-impact::after {
    content: ''; position: absolute; left: 28px; top: 45px; width: 55px; height: 55px;
    background: radial-gradient(circle, #fffde6 0 12%, #ffe594 20%, #ffb64d80 40%, transparent 65%);
    clip-path: polygon(50% 0, 59% 32%, 93% 12%, 70% 44%, 100% 55%, 68% 64%, 80% 100%, 52% 74%, 22% 100%, 33% 62%, 0 45%, 36% 36%);
    animation: knightBattleSpark .7s ease-out infinite;
  }
}
.steel-knight.is-left.is-fighting {
  scale: -.5 .5;
  animation-delay: -1.4s;
  .knight-flail { animation-delay: -1.4s !important; }
  .knight-head { animation-delay: -1.4s; }
  .knight-chain-swing { animation: knightBattleChain 2.8s ease-in-out infinite; animation-delay: -1.4s; }
}
@keyframes knightBattleCombo {
  0%, 100% { transform: translate(0, 0) rotate(0); }
  9% { transform: translate(12px, 3px) rotate(5deg); }
  18% { transform: translate(-34px, -4px) rotate(-7deg); }
  25% { transform: translate(-12px, 0) rotate(2deg); }
  32% { transform: translate(-39px, -2px) rotate(-5deg); }
  43% { transform: translate(12px, 2px) rotate(9deg); }
  53% { transform: translate(19px, 7px) rotate(11deg); }
  63% { transform: translate(-10px, -29px) rotate(-8deg); }
  72% { transform: translate(-41px, -7px) rotate(-9deg); }
  77% { transform: translate(-30px, 4px) rotate(-2deg); }
  87% { transform: translate(8px, 0) rotate(6deg); }
}
@keyframes knightBattleWeaponCombo {
  0%, 100% { transform: rotate(-12deg); }
  10% { transform: rotate(-38deg); }
  18% { transform: rotate(16deg); }
  26% { transform: rotate(-28deg); }
  33% { transform: rotate(12deg); }
  46%, 53% { transform: rotate(-22deg); }
  63% { transform: rotate(65deg); }
  73% { transform: rotate(-8deg); }
  84% { transform: rotate(-28deg); }
}
@keyframes knightBattleChain {
  0%, 100% { transform: rotate(-12deg); }
  12% { transform: rotate(-32deg); }
  22%, 37% { transform: rotate(24deg); }
  48% { transform: rotate(-20deg); }
  64% { transform: rotate(40deg); }
  76% { transform: rotate(-8deg); }
  88% { transform: rotate(-25deg); }
}
@keyframes knightBattleHead {
  0%, 100% { transform: rotate(-4deg); }
  18%, 32% { transform: rotate(-9deg); }
  48% { transform: translateY(3px) rotate(10deg); }
  65% { transform: translateY(-3px) rotate(-7deg); }
  77% { transform: translateY(2px) rotate(3deg); }
}
@keyframes knightBattleGuard {
  0%, 100% { transform: rotate(-8deg); }
  42%, 55% { transform: translate(-12px, -16px) rotate(-24deg); }
  75% { transform: translate(-3px, -4px) rotate(-12deg); }
}
@keyframes knightBattleSpark {
  0%, 65%, 100% { opacity: 0; transform: scale(.3) rotate(0); }
  15% { opacity: 1; transform: scale(1.2) rotate(15deg); }
  40% { opacity: 0; transform: scale(1.5) rotate(30deg); }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight.is-fighting, .steel-knight.is-left.is-fighting,
  .steel-knight.is-fighting .knight-flail,
  .steel-knight.is-left.is-fighting .knight-chain-swing,
  .steel-knight.is-fighting .knight-head,
  .steel-knight.is-fighting .knight-shield,
  .steel-knight.is-fighting .knight-impact b,
  .steel-knight.is-fighting .knight-impact::after { animation: none !important; }
}
.steel-knight.is-warlord {
  top: 35px;
  --steel: linear-gradient(125deg, #e2e0d5, #a8aa9d 24%, #535b53 48%, #c2c3b4 68%, #444c44);
  .knight-cape {
    left: 27px; top: 101px; width: 169px; height: 174px; z-index: -1;
    border: 0; border-radius: 30% 30% 0 0; box-shadow: none;
    background: repeating-linear-gradient(95deg, #321b23 0, #6c303c 25px, #44212c 45px);
    clip-path: polygon(15% 0, 85% 0, 100% 100%, 72% 90%, 47% 100%, 25% 91%, 0 100%);
  }
  .knight-head {
    border-radius: 42% 42% 16% 16%;
    &::before { inset: 0 0 48%; border-radius: 45% 45% 0 0; border-bottom: 4px ridge #b4b7a7; background: var(--steel); z-index: 2; clip-path: polygon(0 0, 100% 0, 100% 88%, 50% 100%, 0 88%); }
    &::after { content: ''; position: absolute; left: 43px; top: 15px; width: 15px; height: 15px; border-radius: 50%; background: var(--steel); border: 2px solid #545d51; z-index: 3; }
  }
  .knight-eye { top: 43px; width: 34px; height: 25px; border-radius: 10% 10% 50% 50%; background: radial-gradient(circle at 50% 35%, #ffb6a6, #df343b 35%, #77212a 65%); border: 7px solid #252c27; }
  .warlord-horns {
    position: absolute; inset: 0;
    i {
      position: absolute; top: -7px; width: 37px; height: 65px;
      filter: drop-shadow(1px 1px 1px #26302780);
      &::before, &::after {
        content: ''; position: absolute; inset: 0;
        clip-path: polygon(0 0, 25% 17%, 32% 45%, 62% 67%, 100% 60%, 92% 87%, 65% 100%, 25% 85%, 10% 54%);
      }
      &::before { background: #303a31; }
      &::after { inset: 3px 2px; background: var(--steel); }
    }
    i:first-child { left: -33px; rotate: -12deg; }
    i:last-child { right: -33px; scale: -1 1; rotate: 12deg; }
    &::after { content: ''; position: absolute; top: -17px; left: 44px; width: 15px; height: 21px; background: var(--steel); clip-path: polygon(50% 0, 100% 100%, 0 100%); }
  }
  .knight-body { border-radius: 15%; i { height: 33px; width: 92%; left: 4%; } i:nth-child(1) { bottom: 6px; } i:nth-child(2) { bottom: 34px; } i:nth-child(3) { bottom: 62px; } }
  .knight-shield { display: none; }
  .arm-right { height: 72px; &::after { content: ''; position: absolute; bottom: -4px; left: 2px; width: 25px; height: 24px; border: 3px ridge #a6af9c; border-radius: 40%; background: radial-gradient(circle, #18251d 0 5px, #8d9882 6px); } }
  .knight-flail-handle { left: -3px; width: 49px; }
  .knight-mace, .knight-mace-collar { display: none; }
  .knight-sword {
    left: -98px; top: 171px; width: 107px; height: 24px;
    border: 0; box-shadow: none;
    background: linear-gradient(0deg, #465548, #a0ad98 43%, #edf0df 49%, #778770 57%, #bbc3ac);
    clip-path: polygon(0 50%, 23% 0, 100% 18%, 100% 82%, 23% 100%);
  }
  .knight-flail-handle::before { content: ''; position: absolute; left: -7px; top: -12px; width: 10px; height: 37px; border-radius: 30%; background: var(--steel); border: 2px solid #45503f; }
}
.steel-knight.is-fighting .knight-battle-label,
.steel-knight.is-fighting .knight-impact { scale: var(--battle-label-scale, 1) 1; }
.steel-knight.is-left.is-fighting {
  animation-name: knightFlailFootwork;
  .knight-flail { animation-name: knightFlailSweep !important; }
  .knight-chain-swing { animation-name: knightChainOrbit; }
}
.steel-knight.is-warlord.is-fighting {
  animation: knightSwordFootwork 2.8s ease-in-out infinite;
  .knight-flail { animation: knightSwordCombo 2.8s ease-in-out infinite !important; }
  .knight-head { animation-delay: -.2s; }
}
@keyframes knightFlailFootwork {
  0%, 100% { transform: translate(0, 0) rotate(0); }
  16% { transform: translate(10px, 5px) rotate(7deg); }
  30% { transform: translate(-24px, -6px) rotate(-6deg); }
  42% { transform: translate(-34px, 1px) rotate(-3deg); }
  55% { transform: translate(15px, 7px) rotate(10deg); }
  70% { transform: translate(-27px, -13px) rotate(-8deg); }
  82% { transform: translate(-36px, 3px) rotate(-4deg); }
  92% { transform: translate(5px, 0) rotate(4deg); }
}
@keyframes knightFlailSweep {
  0%, 100% { transform: rotate(-10deg); }
  15% { transform: rotate(35deg); }
  30% { transform: rotate(-16deg); }
  42% { transform: rotate(16deg); }
  55% { transform: rotate(-35deg); }
  70% { transform: rotate(48deg); }
  82% { transform: rotate(-12deg); }
}
@keyframes knightChainOrbit {
  0%, 100% { transform: rotate(-10deg); }
  18% { transform: rotate(65deg); }
  34% { transform: rotate(-30deg); }
  46% { transform: rotate(28deg); }
  58% { transform: rotate(-40deg); }
  73% { transform: rotate(55deg); }
  86% { transform: rotate(-20deg); }
}
@keyframes knightSwordFootwork {
  0%, 100% { transform: translate(0, 0) rotate(0); }
  10% { transform: translate(-24px, -3px) rotate(-5deg); }
  20% { transform: translate(2px, 0) rotate(3deg); }
  29% { transform: translate(-35px, -2px) rotate(-7deg); }
  39% { transform: translate(9px, 5px) rotate(9deg); }
  50% { transform: translate(17px, -8px) rotate(5deg); }
  63% { transform: translate(-43px, -3px) rotate(-10deg); }
  70% { transform: translate(-35px, 1px) rotate(-7deg); }
  83% { transform: translate(12px, -12px) rotate(7deg); }
}
@keyframes knightSwordCombo {
  0%, 100% { transform: rotate(-15deg); }
  8% { transform: rotate(38deg); }
  15% { transform: rotate(-22deg); }
  26% { transform: rotate(30deg); }
  33% { transform: rotate(-30deg); }
  45% { transform: rotate(50deg); }
  57% { transform: translateX(6px) rotate(-5deg); }
  64%, 70% { transform: translateX(-12px) rotate(-3deg); }
  85% { transform: rotate(32deg); }
}
@media (prefers-reduced-motion: reduce) {
  .steel-knight.is-left.is-fighting, .steel-knight.is-warlord.is-fighting { animation: none; }
}
.steel-knight.is-fighting,
.steel-knight.is-fighting .knight-flail,
.steel-knight.is-fighting .knight-chain-swing,
.steel-knight.is-fighting .knight-head {
  animation-duration: var(--combat-tempo, 2.8s) !important;
}
// Stay ready while approaching; swing only when an opponent is within reach.
.steel-knight.is-fighting:not(.is-in-range) .knight-flail,
.steel-knight.is-fighting:not(.is-in-range) .knight-chain-swing {
  animation: none !important;
  transform: rotate(-8deg);
}
.steel-knight.is-walking.is-awake:not(.is-dragging):not(.is-forming) {
  .knight-leg { transform-origin: 50% 8%; animation: knightStep .42s ease-in-out infinite; }
  .leg-right { animation-delay: -.21s; }
}
@keyframes knightStep {
  0%, 100% { translate: 0 0; rotate: -7deg; }
  25% { translate: -2px -5px; rotate: 8deg; }
  50% { translate: 0 -2px; rotate: 10deg; }
  75% { translate: 2px 1px; rotate: -3deg; }
}
.knight-crown {
  position: absolute; left: 19px; top: -32px; width: 64px; height: 37px;
  z-index: 8; pointer-events: none;
  filter: drop-shadow(0 2px 2px #5c421b80);
  animation: knightCrownArrive .65s ease-out both;
  &::before { content: ''; position: absolute; inset: 0;
    background: linear-gradient(110deg, #b47a22, #ffeaa0 35%, #d6a337 62%, #fff0a0 80%, #a76b1c);
    clip-path: polygon(0 0, 27% 43%, 50% 0, 73% 43%, 100% 0, 87% 100%, 13% 100%);
  }
  &::after { content: ''; position: absolute; bottom: 0; left: 8px; right: 8px; height: 9px; border: 2px solid #b3842b; border-radius: 3px; background: linear-gradient(#fff0a2, #d9a940); }
  i { position: absolute; left: 28px; top: 17px; width: 9px; height: 11px; z-index: 1; border-radius: 45%; background: radial-gradient(circle at 30% 25%, #fff0e3, #df5144 40%, #942b35); box-shadow: -16px 5px 0 -2px #39bfb6, 16px 5px 0 -2px #39bfb6; }
}
@keyframes knightCrownArrive {
  0% { opacity: 0; transform: translateY(-25px) scale(1.2); }
  65% { opacity: 1; transform: translateY(3px) scale(1); }
  100% { opacity: 1; transform: translateY(0); }
}
.steel-knight.is-crowned {
  --steel: linear-gradient(125deg, #fff2b5, #d8b358 24%, #8a641e 48%, #f4d57b 68%, #9e7629);
  filter: drop-shadow(0 0 6px #e9bf4770) drop-shadow(3px 8px 5px #47341550);
  .knight-head, .knight-body, .knight-shoulder, .knight-arm, .knight-leg,
  .knight-hand, .knight-free-hand, .knight-shield, .knight-flail-handle,
  .knight-mace-collar, .knight-mace, .shield-skull,
  .shoulder-right::before, .shoulder-right::after,
  .knight-body i, .warlord-horns i::after, .shoulder-right b::before {
    background: var(--steel) !important;
    border-color: #806026;
    box-shadow: inset 2px 2px 3px #fff0a4, inset -3px -3px 5px #78501f;
  }
  .knight-chain i { border-color: #d4b25e; }
  .knight-sword { background: linear-gradient(0deg, #8a641e, #e9c86d 43%, #fff7ce 49%, #b68d33 57%, #edd584); }
  .knight-leg::after, .knight-flail-handle::before { border-color: #806026; }
}
.steel-knight.is-fighting {
  &.skill-charge, &.skill-thrust { animation: knightSkillCharge 1s ease-in-out both !important; }
  &.skill-combo { animation: knightSkillComboStep 1s ease-in-out both !important; }
  &.skill-sweep { animation: knightSkillWindup 1s ease-in-out both !important; }
  &.skill-slam { animation: knightSkillWindup 1s ease-in-out both !important; }
  &.skill-jump { animation: knightSkillJump 1s ease-in-out both !important; }
  &.skill-guard { animation: knightSkillBrace 1s ease-in-out both !important; }
  &.skill-charge .knight-flail, &.skill-thrust .knight-flail { animation: knightSkillThrust 1s ease-in-out both !important; }
  &.skill-combo .knight-flail { animation: knightSkillCombo .25s ease-in-out 4 !important; }
  &.skill-sweep .knight-chain-swing { animation: knightSkillSweep 1s ease-in-out both !important; }
  &.skill-jump .knight-flail, &.skill-slam .knight-flail { animation: knightSkillSlam 1s ease-in-out both !important; }
  &.skill-guard .knight-shield { animation: knightSkillShield 1s ease-in-out both !important; }
  &.skill-guard .knight-flail { animation: none !important; transform: rotate(-25deg); }
}
@keyframes knightSkillCharge {
  0%, 100% { transform: translateX(0) rotate(0); }
  25% { transform: translate(24px, 8px) rotate(14deg); }
  42%, 60% { transform: translate(calc(-1 * var(--dash-distance)), -2px) rotate(-18deg); }
  75% { transform: translate(-40px, 3px) rotate(-7deg); }
}
@keyframes knightSkillJump {
  0%, 100% { transform: translate(0, 0); }
  20% { transform: translate(8px, 12px) rotate(8deg); }
  42% { transform: translate(-15px, -125px) rotate(-16deg); }
  55% { transform: translate(-34px, -105px) rotate(-20deg); }
  72% { transform: translate(-48px, 10px) rotate(-8deg); }
  82% { transform: translate(-30px, -5px) rotate(-3deg); }
}
@keyframes knightSkillBrace { 0%, 100% { transform: rotate(0); } 20%, 75% { transform: translate(10px, 10px) rotate(12deg); } 44%, 58% { transform: translate(24px, 12px) rotate(18deg); } }
@keyframes knightSkillThrust { 0%, 100% { transform: rotate(-12deg); } 25% { transform: translateX(12px) rotate(-30deg); } 43%, 62% { transform: translateX(-35px) rotate(0); } }
@keyframes knightSkillCombo { 0%, 100% { transform: rotate(-42deg); } 35% { transform: rotate(48deg); } 65% { transform: translateX(-15px) rotate(-30deg); } }
@keyframes knightSkillSweep { 0%, 100% { transform: rotate(-25deg); } 25% { transform: rotate(-65deg); } 50% { transform: rotate(140deg); } 75% { transform: rotate(-50deg); } }
@keyframes knightSkillSlam { 0%, 100% { transform: rotate(-12deg); } 30%, 48% { transform: rotate(120deg); } 69% { transform: rotate(-38deg); } 80% { transform: rotate(-20deg); } }
@keyframes knightSkillShield { 0%, 100% { transform: rotate(-8deg); } 22%, 76% { transform: translate(-120px, -30px) rotate(-20deg); } 45%, 58% { transform: translate(-108px, -24px) rotate(-10deg); } }
@keyframes knightSkillComboStep { 0%, 100% { transform: translateX(0); } 25% { transform: translate(-20px, -4px) rotate(-5deg); } 50% { transform: translate(-40px, 0) rotate(-10deg); } 75% { transform: translate(-56px, -4px) rotate(-6deg); } }
@keyframes knightSkillWindup { 0%, 100% { transform: rotate(0); } 30% { transform: translate(15px, 7px) rotate(17deg); } 65% { transform: translate(-32px, -5px) rotate(-18deg); } 80% { transform: translate(-18px, 4px) rotate(-8deg); } }
.steel-knight.is-fighting {
  &.skill-shield-throw {
    animation: knightSkillWindup 1s ease-in-out both !important;
    .knight-shield { animation: knightShieldThrow 1s ease-in-out both !important; }
  }
  &.skill-tornado {
    animation: knightTornadoSpin 1s linear both !important;
    .knight-flail { animation: knightTornadoWeapon .25s linear 4 !important; }
    .knight-chain-swing { animation: knightTornadoChain .25s linear 4 !important; }
  }
  &.skill-blade-wave {
    animation: knightSkillWindup 1s ease-in-out both !important;
    .knight-flail { animation: knightSkillSlam 1s ease-in-out both !important; }
  }
}
.knight-tornado {
  position: absolute; left: -92px; top: -45px; width: 400px; height: 365px;
  z-index: 8; pointer-events: none;
  i { position: absolute; left: 50%; border: 7px solid #647c80; border-left-color: transparent; border-right-color: #c5d5ce; border-radius: 50%; animation: knightWindOrbit .25s linear 4; }
  @for $ring from 1 through 5 {
    i:nth-child(#{$ring}) { width: 370px - ($ring - 1) * 36px; height: 85px; margin-left: -185px + ($ring - 1) * 18px; top: ($ring - 1) * 69px; animation-delay: ($ring - 1) * -.04s; }
  }
}
.knight-blade-wave {
  position: absolute; left: -40px; top: 70px; width: 75px; height: 205px;
  border-left: 15px solid #e5ede4; border-right: 5px solid #597c78;
  border-radius: 50%; z-index: 8; pointer-events: none;
  animation: knightBladeWave 1.4s ease-out both;
  &::before, &::after { content: ''; position: absolute; top: 15px; bottom: 15px; width: 65px; border-left: 7px solid #93b7af; border-radius: 50%; }
  &::before { left: 15px; }
  &::after { left: 35px; top: 35px; bottom: 35px; border-left-width: 4px; border-left-color: #647f78; }
}
@keyframes knightShieldThrow {
  0%, 100% { transform: translate(0, 0) rotate(-8deg); }
  20% { transform: translate(-60px, -24px) rotate(-40deg); }
  50% { transform: translate(calc(-1 * var(--projectile-distance) - 40px), -16px) rotate(-400deg); }
  70% { transform: translate(calc(-.65 * var(--projectile-distance)), -10px) rotate(-650deg); }
}
@keyframes knightTornadoSpin { 0%, 100% { transform: translateX(0) rotate(0); } 25% { transform: translate(-32px, -10px) rotate(-22deg); } 50% { transform: translate(-64px, -18px) rotate(22deg); } 75% { transform: translate(-38px, -8px) rotate(-22deg); } }
@keyframes knightTornadoWeapon { from { transform: rotate(-45deg); } to { transform: rotate(315deg); } }
@keyframes knightTornadoChain { from { transform: rotate(0) scale(1.15); } to { transform: rotate(360deg) scale(1.15); } }
@keyframes knightWindOrbit { from { transform: rotate(-12deg) scaleX(.8); opacity: .5; } to { transform: rotate(12deg) scaleX(1.1); opacity: .9; } }
@keyframes knightBladeWave { 0%, 40% { opacity: 0; transform: translateX(0) scale(.4); } 55% { opacity: 1; transform: translateX(-35px) scale(1); } 100% { opacity: 0; transform: translateX(calc(-1 * var(--projectile-distance))) scale(1.2); } }
.steel-knight.is-fetching-shield {
  animation: none !important;
  .knight-flail, .knight-chain-swing { animation: none !important; transform: rotate(-15deg); }
}
.steel-knight.is-fighting.skill-shield-retreat,
.steel-knight.is-fighting.skill-blade-retreat {
  animation: none !important;
  .knight-flail { animation: none !important; transform: rotate(-20deg); }
  .knight-shield { animation: none !important; transform: rotate(-8deg); }
}
.steel-knight.is-lancer {
  top: 35px;
  .knight-head {
    border-radius: 46% 46% 12% 12%;
    &::before { inset: 4px 5px 55%; border-radius: 70% 70% 0 0; background: repeating-linear-gradient(75deg, transparent 0, transparent 15px, #38463a 16px, transparent 18px); border-bottom: 3px ridge #b6bcae; }
  }
  .helmet-horns, .knight-eye, .knight-nose, .knight-mouth,
  .knight-mace, .knight-mace-collar { display: none; }
  .helmet-visor { top: 45px; height: 14px; border-radius: 35%; }
  .helmet-plume {
    position: absolute; left: 112px; top: -18px; width: 65px; height: 155px;
    z-index: 0; pointer-events: none; border-radius: 65% 70% 20% 20%;
    background: repeating-linear-gradient(100deg, #702b3b 0, #702b3b 8px, #9b4251 11px, #4c2431 16px);
    clip-path: polygon(8% 5%, 48% 0, 82% 12%, 100% 40%, 92% 100%, 67% 88%, 48% 96%, 38% 55%, 0 24%);
    transition: translate 1.2s ease, rotate 1.2s ease;
  }
  &.is-scattered .helmet-plume { translate: -17px 95px; rotate: -22deg; }
  .knight-cape { left: 28px; top: 110px; width: 164px; height: 165px; z-index: -1; border: 0; background: repeating-linear-gradient(100deg, #532431 0, #893748 25px, #532431 45px); clip-path: polygon(10% 0, 90% 0, 100% 100%, 60% 92%, 30% 100%, 0 95%); }
  .knight-body { background: var(--steel); i { width: 27%; height: 65px; } }
  .knight-flail-handle { left: -6px; width: 52px; background: linear-gradient(#384038, #58634e, #232b23); }
  .knight-lance {
    left: -198px; top: 160px; width: 200px; height: 45px; border: 0; box-shadow: none;
    background: linear-gradient(0deg, #414d42, #b2baa7 43%, #f1f1df 49%, #7a8771 57%, #bbc2ad);
    clip-path: polygon(0 50%, 100% 0, 100% 100%);
    i { position: absolute; left: 65px; top: 21px; width: 135px; height: 3px; background: #e6e8d3; }
  }
  .knight-flail-handle::before { content: ''; position: absolute; left: -12px; top: -16px; width: 18px; height: 44px; border-radius: 50%; border: 3px ridge #c2c7b4; background: var(--steel); }
  &.is-fighting { animation: knightSwordFootwork 2.8s ease-in-out infinite; .knight-flail { animation: knightBattleStrike 2.8s ease-in-out infinite !important; } }
  &.is-fighting.skill-thrust .knight-flail, &.is-fighting.skill-charge .knight-flail { animation: knightSkillThrust 1s ease-in-out both !important; }
  &.is-fighting.skill-jump .knight-flail { animation: knightSkillSlam 1s ease-in-out both !important; }
  &.is-fighting.skill-combo .knight-flail { animation: knightSkillCombo .25s ease-in-out 4 !important; }
  &.is-fighting.skill-poke {
    animation: knightLancePokeStep 1s ease-in-out both !important;
    .knight-flail { animation: knightLancePoke 1s ease-in-out both !important; }
  }
  &.is-fighting.skill-dodge {
    animation: knightLanceDodge 1s ease-in-out both !important;
    .knight-flail { animation: none !important; transform: rotate(30deg); }
  }
  &.is-fighting.skill-lance-dive {
    animation: knightLanceDive 1.8s linear both !important;
    .knight-flail { animation: knightLanceAim 1.8s linear both !important; }
    .knight-leg { animation: none; }
  }
  &.is-fighting:not(.is-in-range):not([class*="skill-"]) .knight-flail { animation: none !important; transform: rotate(-8deg); }
}
@keyframes knightBattleStrike { 0%, 100% { transform: rotate(-8deg); } 30% { transform: translateX(-22px) rotate(-3deg); } 60% { transform: rotate(-20deg); } 75% { transform: translateX(-12px) rotate(5deg); } }
@keyframes knightLancePokeStep { 0%, 100% { transform: translateX(0); } 35%, 55% { transform: translateX(-18px) rotate(-4deg); } }
@keyframes knightLancePoke { 0%, 100% { transform: rotate(-8deg); } 35%, 50% { transform: translateX(-26px) rotate(-2deg); } 70% { transform: translateX(5px) rotate(-8deg); } }
@keyframes knightLanceDodge { 0%, 100% { transform: translate(0, 0) rotate(0); } 20% { transform: translate(32px, -8px) rotate(12deg); } 40%, 60% { transform: translate(72px, 6px) rotate(23deg); } 80% { transform: translate(30px, 0) rotate(8deg); } }
@keyframes knightLanceDive {
  0%, 100% { transform: translate(0, 0) rotate(0); }
  12% { transform: translate(8px, 10px) rotate(6deg); }
  30% { transform: translate(0, -165px) rotate(-8deg); }
  34%, 56% { transform: translate(0, -170px) rotate(-12deg); }
  74% { transform: translate(calc(-1 * var(--dash-distance)), 8px) rotate(-22deg); }
  82% { transform: translate(calc(-1 * var(--dash-distance)), 3px) rotate(-12deg); }
  92% { transform: translate(calc(-.5 * var(--dash-distance)), -5px) rotate(-4deg); }
}
@keyframes knightLanceAim {
  0%, 100% { transform: rotate(-8deg); }
  28% { transform: rotate(30deg); }
  34%, 56% { transform: rotate(-60deg); }
  74%, 82% { transform: rotate(-45deg); }
  92% { transform: rotate(-18deg); }
}
.steel-knight.is-falling {
  pointer-events: none;
  animation: knightDefeatFall 1.5s ease-in both !important;
  transform-origin: 50% 100%;
  .knight-head, .knight-flail, .knight-chain-swing, .knight-leg, .knight-eye { animation: none !important; }
  .knight-eye { filter: brightness(.4); }
}
@keyframes knightDefeatFall {
  0% { transform: translate(0, 0) rotate(0); }
  12% { transform: translate(22px, -8px) rotate(12deg); }
  24% { transform: translate(36px, -5px) rotate(5deg); }
  38% { transform: translate(45px, 0) rotate(30deg); }
  60% { transform: translate(60px, 8px) rotate(88deg); }
  70% { transform: translate(60px, -4px) rotate(78deg); }
  82%, 100% { transform: translate(60px, 8px) rotate(88deg); }
}
.steel-knight.is-dragging .knight-flail,
.steel-knight.is-dragging .knight-chain-swing,
.steel-knight.is-victorious .knight-chain-swing { animation: none !important; }
@media (prefers-reduced-motion: reduce) {
  .steel-knight,
  .steel-knight[class] *,
  .steel-knight[class] *::before,
  .steel-knight[class] *::after {
    animation: none !important;
    transition: none !important;
  }
}
@media (max-width: 1799px), (max-height: 799px) {
  body.classMasterHead .steel-knight { display: none; }
}
</style>
